// Turning ProjectStats into the console's KPI rows and charts: metric definitions (label, format,
// which way is better), the bar chart for one metric over the range, and the generic Usage tab.
import type { ReactNode } from "react";
import { DailyBars } from "../../charts/Chart.tsx";
import { Stacked } from "../../charts/Stacked.tsx";
import { fmt } from "../../fmt.ts";
import type { Fact, Metric, ProjectStats, Range } from "../../data/stats.ts";
import { SERIES } from "../../shell/nav.tsx";
import { BarList, Failed, Loading, Stats, type StatDef } from "../../ui.tsx";
import { useProject } from "./ProjectPage.tsx";

export interface MetricDef { label: string; unit: string; format: (n: number) => string; good: "up" | "down" | "none"; gauge?: boolean }
export const METRICS: Record<Metric, MetricDef> = {
  requests: { label: "Requests", unit: "requests", format: fmt.compact, good: "up" },
  errors: { label: "Errors", unit: "errors", format: fmt.int, good: "down" },
  cpuMs: { label: "CPU time", unit: "ms", format: fmt.ms, good: "none" },
  docReads: { label: "Document reads", unit: "reads", format: fmt.compact, good: "up" },
  docWrites: { label: "Document writes", unit: "writes", format: fmt.compact, good: "up" },
  docDeletes: { label: "Document deletes", unit: "deletes", format: fmt.compact, good: "none" },
  queries: { label: "Queries", unit: "queries", format: fmt.compact, good: "up" },
  denied: { label: "Rule denials", unit: "denials", format: fmt.int, good: "down" },
  rowsRead: { label: "Rows read", unit: "rows", format: fmt.compact, good: "none" },
  rowsWritten: { label: "Rows written", unit: "rows", format: fmt.compact, good: "none" },
  dbBytes: { label: "Database size", unit: "", format: fmt.bytes, good: "none", gauge: true },
  users: { label: "Users", unit: "users", format: fmt.int, good: "up", gauge: true },
  signups: { label: "Sign-ups", unit: "sign-ups", format: fmt.int, good: "up" },
  activeUsers: { label: "Active users", unit: "users", format: fmt.int, good: "up" },
  fileBytes: { label: "Stored files", unit: "", format: fmt.bytes, good: "none", gauge: true },
  fileObjects: { label: "Files", unit: "files", format: fmt.int, good: "none", gauge: true },
  fileOpsA: { label: "Uploads and lists", unit: "ops", format: fmt.compact, good: "none" },
  fileOpsB: { label: "Downloads", unit: "ops", format: fmt.compact, good: "none" },
  rtOpens: { label: "Connections", unit: "connections", format: fmt.compact, good: "up" },
  rtMinutes: { label: "Connection minutes", unit: "min", format: fmt.compact, good: "none" },
  rtSnaps: { label: "Snapshots delivered", unit: "snapshots", format: fmt.compact, good: "up" },
  rtPeak: { label: "Peak connected", unit: "clients", format: fmt.int, good: "up", gauge: true },
  emails: { label: "Emails sent", unit: "emails", format: fmt.int, good: "up" },
  emailsFailed: { label: "Emails failed", unit: "emails", format: fmt.int, good: "down" },
  emailsBounced: { label: "Bounced", unit: "emails", format: fmt.int, good: "down" },
  emailsComplained: { label: "Complaints", unit: "emails", format: fmt.int, good: "down" },
  pushes: { label: "Pushes sent", unit: "pushes", format: fmt.int, good: "up" },
  pushesInvalid: { label: "Dead tokens", unit: "tokens", format: fmt.int, good: "down" },
  pushesFailed: { label: "Pushes failed", unit: "pushes", format: fmt.int, good: "down" },
  fnCalls: { label: "Function calls", unit: "calls", format: fmt.compact, good: "up" },
  fnErrors: { label: "Function errors", unit: "errors", format: fmt.int, good: "down" },
};

const VS: Record<Range, string> = { "24h": "the 24h before", "7d": "the 7 days before", "30d": "the 30 days before", "90d": "the 90 days before" };
export const spanLabel = (r: Range) => ({ "24h": "in the last 24 hours", "7d": "in the last 7 days", "30d": "in the last 30 days", "90d": "in the last 90 days" })[r];

/** KPI tiles for these metrics from the loaded stats (the total over the range, with deltas). */
export function kpis(s: ProjectStats | undefined, metrics: Metric[], hrefs: Partial<Record<Metric, string>> = {}): StatDef[] {
  return metrics.map((m) => {
    const d = METRICS[m];
    const t = s?.kpis[m];
    const series = s?.series[m];
    const value = t ? t.now : series ? (d.gauge ? series[series.length - 1] : series.reduce((a, b) => a + b, 0)) : undefined;
    // Loaded stats without this metric yet read as 0; "–" is for still loading.
    return { label: d.label, value: value ?? (s ? 0 : null), format: d.format, trend: t, good: d.good, vs: s ? VS[s.range] : undefined, href: hrefs[m] };
  });
}

export function MetricBars({ s, metric, title, empty }: { s: ProjectStats; metric: Metric; title?: string; empty?: string }) {
  const d = METRICS[metric];
  const vals = s.series[metric] ?? [];
  const points = s.buckets.map((day, i) => ({ day, value: vals[i] ?? 0 }));
  const has = vals.some((v) => v > 0);
  return <DailyBars title={title ?? d.label} unit={d.unit} format={d.format} points={has ? points : []} span={spanLabel(s.range)} empty={empty ?? noData(s, metric)} />;
}

/** Why a metric has no numbers yet, in words that say what to do. */
export function noData(s: ProjectStats, metric: Metric): string {
  if (s.fallback) {
    if (["requests", "rowsRead", "rowsWritten", "dbBytes", "emails", "emailsFailed", "emailsBounced", "emailsComplained"].includes(metric)) return "Nothing yet in this range.";
    return "Detailed numbers start once your backend is updated for the new console. Nothing to do on your side; it happens with the next update.";
  }
  return s.sources.ae || s.sources.tenant ? "Nothing yet in this range." : "Collecting. The first numbers appear within the hour.";
}

/** A breakdown as stacked daily bars when the stats have per-day facts, else a bar list. */
export function FactList({ s, fact, title, format = fmt.compact, empty }: { s: ProjectStats; fact: string; title: string; format?: (n: number) => string; empty?: string }) {
  const items: Fact[] = (s.facts[fact] ?? []).slice(0, 10);
  return (
    <figure className="chart">
      <figcaption><div><span className="chart-title">{title}</span><span className="chart-total">{items.length ? `top ${items.length} ${spanLabel(s.range)}` : ""}</span></div></figcaption>
      <BarList items={items} format={format} empty={empty ?? (s.fallback ? noData(s, "docReads") : "Nothing yet in this range.")} />
    </figure>
  );
}

export function StackedMetrics({ s, metrics, title, mode = "bars" }: { s: ProjectStats; metrics: Metric[]; title: string; mode?: "bars" | "area" }) {
  const layers = metrics.map((m, i) => ({ id: m, label: METRICS[m].label, color: SERIES[i]!, values: s.series[m] ?? s.buckets.map(() => 0) }));
  if (!layers.some((l) => l.values.some((v) => v > 0))) return <MetricBars s={s} metric={metrics[0]!} title={title} />;
  return <Stacked title={title} days={s.buckets} layers={layers} format={METRICS[metrics[0]!].format} mode={mode} />;
}

/** The standard Usage tab: a KPI row and one bar chart per metric, plus any breakdowns. */
export function UsageTab({ metrics, facts = [], extra }: { metrics: Metric[]; facts?: { fact: string; title: string; format?: (n: number) => string }[]; extra?: ReactNode }) {
  const { stats } = useProject();
  if (stats.error) return <Failed error={stats.error} retry={stats.reload} />;
  const s = stats.data;
  if (!s) return <Loading />;
  return (
    <>
      <Stats items={kpis(s, metrics.slice(0, 4))} />
      {s.fallback && <p className="notice small" style={{ marginBottom: 20 }}>Showing daily totals. Hourly and per-collection numbers start with your backend's next update.</p>}
      <div className="charts">
        {metrics.map((m) => <MetricBars key={m} s={s} metric={m} />)}
        {facts.map((f) => <FactList key={f.fact} s={s} fact={f.fact} title={f.title} format={f.format} />)}
      </div>
      {extra}
    </>
  );
}
